/**
 * Frontend Logger Utility
 * Only logs in development mode to prevent debug info in production
 */

const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';

interface Logger {
  log: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  group: (label: string) => void;
  groupEnd: () => void;
  table: (data: unknown) => void;
}

/**
 * Create a namespaced logger for a specific component/module
 */
export const createLogger = (namespace: string): Logger => ({
  log: (...args: unknown[]) => {
    if (isDev) console.log(`[${namespace}]`, ...args);
  },
  debug: (...args: unknown[]) => {
    if (isDev) console.debug(`[${namespace}]`, ...args);
  },
  info: (...args: unknown[]) => {
    if (isDev) console.info(`[${namespace}]`, ...args);
  },
  warn: (...args: unknown[]) => {
    // Warnings are shown in both dev and prod
    console.warn(`[${namespace}]`, ...args);
  },
  error: (...args: unknown[]) => {
    // Errors are always shown
    console.error(`[${namespace}]`, ...args);
  },
  group: (label: string) => {
    if (isDev) console.group(`[${namespace}] ${label}`);
  },
  groupEnd: () => {
    if (isDev) console.groupEnd();
  },
  table: (data: unknown) => {
    if (isDev) console.table(data);
  },
});

/**
 * Default logger for general use
 */
export const logger: Logger = {
  log: (...args: unknown[]) => {
    if (isDev) console.log(...args);
  },
  debug: (...args: unknown[]) => {
    if (isDev) console.debug(...args);
  },
  info: (...args: unknown[]) => {
    if (isDev) console.info(...args);
  },
  warn: (...args: unknown[]) => {
    console.warn(...args);
  },
  error: (...args: unknown[]) => {
    console.error(...args);
  },
  group: (label: string) => {
    if (isDev) console.group(label);
  },
  groupEnd: () => {
    if (isDev) console.groupEnd();
  },
  table: (data: unknown) => {
    if (isDev) console.table(data);
  },
};

export default logger;
